import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import './Commission.css';

const unitLogo = '/images/LP_Unit_logo.png';

const ITEM_PRICE = 20;
const PREMIUM_ASSET_PRICE = 10;
const ACEX_PRICE = 10;

function HeadwearCommission() {
  const { user, userData, logout } = useAuth();
  const [showInfoPopup, setShowInfoPopup] = useState(true);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // User info from popup
  const [zap, setZap] = useState('');
  const [discordName, setDiscordName] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');

  // Cart items
  const [items, setItems] = useState([]);
  const [comments, setComments] = useState('');

  // Premium options
  const [premiumAsset, setPremiumAsset] = useState(false);
  const [customAcex, setCustomAcex] = useState(false);

  // Add item form
  const [newItemType, setNewItemType] = useState('Baseball Cap');
  const [newItemSpec, setNewItemSpec] = useState('');
  const [newItemImageLink, setNewItemImageLink] = useState('');

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (zap && discordName && paypalEmail) {
      setShowInfoPopup(false);
    }
  };

  const addItem = () => {
    if (!newItemSpec.trim()) {
      alert('Please specify details for your headwear.');
      return;
    }
    
    const newItem = {
      id: Date.now(),
      type: newItemType,
      specification: newItemSpec,
      imageLink: newItemImageLink,
      price: ITEM_PRICE
    };
    
    setItems([...items, newItem]);
    setNewItemSpec('');
    setNewItemImageLink('');
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    let total = items.length * ITEM_PRICE;
    if (premiumAsset) total += PREMIUM_ASSET_PRICE;
    if (customAcex) total += ACEX_PRICE;
    return total;
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      alert('Please add at least one item to your order.');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'requests'), {
        userId: user.uid,
        userEmail: userData?.email || '',
        username: userData?.username || '',
        type: 'New - Headwear',
        status: 'Unassigned',
        zap,
        discordName,
        paypalEmail,
        items: items.map(item => ({
          type: item.type,
          specification: item.specification,
          imageLink: item.imageLink
        })),
        premiumAsset,
        customAcex,
        totalPrice: calculateTotal(),
        comments,
        createdAt: Timestamp.now()
      });

      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <nav className="commission-navbar">
          <Link to="/" className="nav-left">
            <img src={unitLogo} alt="Logo" className="unit-logo" />
            <h1 className="site-title">Creature Supply</h1>
          </Link>
        </nav>
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h2>Commission Submitted!</h2>
            <p>Your headwear order has been received.</p>
            <p className="success-details">ZAP: {zap}</p>
            <p className="success-details">Items: {items.length}</p>
            <p className="success-details">Total: ${calculateTotal()}</p>
            <p className="success-details">We'll notify you when your order is ready.</p>
            <Link to="/" className="btn-home">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Navigation */}
      <nav className="commission-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
        </div>
      </nav>

      {/* Info Popup */}
      {showInfoPopup && (
        <div className="popup-overlay">
          <div className="popup-card">
            <form onSubmit={handleInfoSubmit}>
              <div className="popup-field">
                <label>Your ZAP Number</label>
                <input 
                  type="text" 
                  value={zap}
                  onChange={(e) => setZap(e.target.value)}
                  placeholder="Enter your ZAP"
                  required
                />
              </div>
              <div className="popup-field">
                <label>Discord Name</label>
                <input 
                  type="text" 
                  value={discordName}
                  onChange={(e) => setDiscordName(e.target.value)}
                  placeholder="J. Smith"
                  required
                />
              </div>
              <div className="popup-field">
                <label>PayPal Email</label>
                <input 
                  type="email" 
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <button type="submit" className="popup-btn">Continue</button>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!showInfoPopup && (
        <div className="commission-content">
          <div className="commission-layout">
            {/* Left Side - Item Selection */}
            <div className="selection-panel">
              <h2 className="panel-title">Custom Headwear</h2>
              
              {/* Add Item Form */}
              <div className="dropdown-section">
                <div className="dropdown-header">Add Headwear</div>
                
                <div className="add-item-form">
                  <div className="form-row">
                    <label>Headwear Type</label>
                    <select 
                      value={newItemType}
                      onChange={(e) => setNewItemType(e.target.value)}
                      className="item-select"
                    >
                      <option value="Baseball Cap">Baseball Cap - $20</option>
                      <option value="Beanie">Beanie - $20</option>
                      <option value="Boonie Hat">Boonie Hat - $20</option>
                      <option value="Patrol Cap">Patrol Cap - $20</option>
                      <option value="Other/Custom">Other/Custom - $20</option>
                    </select>
                  </div>
                  
                  <div className="form-row">
                    <label>Description</label>
                    <textarea 
                      value={newItemSpec}
                      onChange={(e) => setNewItemSpec(e.target.value)}
                      placeholder="Describe your headwear (color, style, design, etc.)"
                      className="item-textarea"
                    />
                  </div>
                  
                  <div className="form-row">
                    <label>Reference Link/Image URL</label>
                    <input 
                      type="text"
                      value={newItemImageLink}
                      onChange={(e) => setNewItemImageLink(e.target.value)}
                      placeholder="https://..."
                      className="item-input"
                    />
                  </div>
                  
                  <button 
                    type="button"
                    onClick={addItem}
                    className="btn-add-item"
                  >
                    + Add to Cart
                  </button>
                </div>
              </div>

              {/* Items List */}
              {items.length > 0 && (
                <div className="dropdown-section">
                  <div className="dropdown-header">Your Items ({items.length})</div>
                  <div className="items-list">
                    {items.map((item, index) => (
                      <div key={item.id} className="item-row">
                        <div className="item-info">
                          <span className="item-number">{index + 1}.</span>
                          <span className="item-type">{item.type}</span>
                          <span className="item-price">${item.price}</span>
                        </div>
                        <div className="item-spec">{item.specification}</div>
                        <button 
                          onClick={() => removeItem(item.id)}
                          className="btn-remove-item"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Premium Options */}
              <div className="dropdown-section">
                <div className="dropdown-header">Additional Options</div>
                <div className="premium-options">
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={premiumAsset}
                      onChange={(e) => setPremiumAsset(e.target.checked)}
                    />
                    <span className="checkbox-label">Premium Asset</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={customAcex}
                      onChange={(e) => setCustomAcex(e.target.checked)}
                    />
                    <span className="checkbox-label">Custom ACEX Options</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                </div>
              </div>

              {/* Comments */}
              <div className="dropdown-section">
                <div className="dropdown-header">Additional Comments</div>
                <textarea 
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Any additional notes..."
                  className="comments-textarea"
                />
              </div>
            </div>

            {/* Right Side - Cart and Submit */}
            <div className="preview-panel">
              <div className="preview-header">Headwear Commission</div>
              
              <div className="preview-box">
                <div className="preview-placeholder">
                  <span>Add items to see your order summary</span>
                </div>
              </div>

              {/* Cart Section */}
              <div className="cart-section">
                <h3 className="cart-title">Cart</h3>
                
                {items.length === 0 ? (
                  <p className="cart-empty">No items added yet</p>
                ) : (
                  <div className="cart-items">
                    {items.map((item, index) => (
                      <div key={item.id} className="cart-line">
                        <span>{item.type}</span>
                        <span>${item.price}</span>
                      </div>
                    ))}
                  </div>
                )}

                {premiumAsset && (
                  <div className="cart-line addon">
                    <span>Premium Asset</span>
                    <span>+${PREMIUM_ASSET_PRICE}</span>
                  </div>
                )}

                {customAcex && (
                  <div className="cart-line addon">
                    <span>Custom ACEX</span>
                    <span>+${ACEX_PRICE}</span>
                  </div>
                )}

                <div className="cart-total">
                  <span>Total</span>
                  <span>${calculateTotal()}</span>
                </div>
              </div>

              <button 
                className="submit-btn"
                onClick={handleSubmit}
                disabled={submitting || items.length === 0}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HeadwearCommission;
